<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("update_email_template") }}</template>
		</core-layout-headline-and-actions>

		<!-- Body -->
		<div class="flex flex-row xs-12">
			<!-- Left -->
			<div class="xs-6 mr1">
				<!-- Tabs -->
				<div class="flex flex-row xs-12 xs-left">
					<j-tabs
						:selectedTab="toggleSetup"
						:tabs="[
							{
								name: $t('editor'),
								value: 'editor'
							},
							{
								name: $t('attachments'),
								value: 'attachments'
							},
							{
								name: $t('settings'),
								value: 'settings'
							}
						]"
						@click="toggleSetup = $event"
					/>
				</div>
				<div>
					<!-- Editor -->
					<template-editor
						v-if="toggleSetup === 'editor'"
						:payload="computedEditorPayload"
						@updated:mjml="updateMjml($event)"
						@updated:graphQlQuery="updateGraphQlQuery($event)"
						@updated:graphQlVariables="updateGraphQlVariables($event)"
					/>

					<!-- Attachments -->
					<template-attachments
						v-if="toggleSetup === 'attachments'"
						:listOfTemplates="fetchedPdfTemplates"
						:attachedTemplates="template.attachedTemplates"
						:attachedFiles="template.attachedFiles"
						@added:attachment="fetchEmailTemplate"
						@removed:attachment="fetchEmailTemplate"
						@updated:attachment="fetchEmailTemplate"
					/>
					<!-- Settings -->
					<template-settings
						v-if="toggleSetup === 'settings'"
						currentType="update"
						:payload="template"
					/>
				</div>
			</div>

			<!-- Right -->
			<j-card class="xs-6 ml1 p4">
				<div class="flex flex-row xs-12 xs-right mb4">
					<!-- Buttons -->
					<j-button
						size="small"
						class="mr2"
						:type="togglePreview !== 'desktop' ? 'secondary' : 'primary'"
						@click="togglePreview = 'desktop'"
						>{{ $t("desktop") }}</j-button
					>
					<j-button
						size="small"
						class="mr2"
						:type="togglePreview !== 'mobile' ? 'secondary' : 'primary'"
						@click="togglePreview = 'mobile'"
						>{{ $t("mobile") }}</j-button
					>
					<j-button
						size="small"
						:type="togglePreview !== 'send_test' ? 'secondary' : 'primary'"
						@click="togglePreview = 'send_test'"
						>{{ $t("send_test") }}</j-button
					>
				</div>

				<!-- Errors from post request -->
				<div
					v-if="togglePreview === 'desktop' || togglePreview === 'mobile'"
					class="mb2 pl5 pr5"
				>
					<ul>
						<li
							class="fs3 fw7 cError"
							v-for="(error, i) in computedRenderErrors"
							:key="i"
						>
							{{ error }}
						</li>
					</ul>
				</div>

				<!-- Preview -->
				<j-card
					v-if="togglePreview === 'desktop' || togglePreview === 'mobile'"
					class="flex xs-12 xs-center b1 bc7 bg5"
				>
					<h2 v-if="render.html === null" class="fs4 p4 c6">
						{{ $t("no_preview_available") }}
					</h2>
					<div
						v-else
						:class="{
							'xs-12': togglePreview === 'desktop',
							'xs-6': togglePreview === 'mobile'
						}"
						v-html="render.html"
					></div>
				</j-card>

				<!-- Send Test Email -->
				<template-send-test
					v-if="togglePreview === 'send_test'"
					:template="template"
					:graphQlVariables="computedGraphQlVariables"
				/>
			</j-card>
		</div>

		<!-- Bottom -->
		<div class="mt4 p4 bt1 bc7 flex xs-12 xs-right">
			<j-button type="error" @click="$modal.show('j-dialog')">{{
				$t("delete_template")
			}}</j-button>
		</div>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_template')"
			type="delete"
			@confirm="deleteTemplate"
			>{{ $t("errors.are_you_sure_you_want_to_delete_template") }}</j-dialog
		>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JButton from "@/components/JButton"
import JBaseToggle from "@/components/JBaseToggle"
import JCard from "@/components/JCard"
import JTabs from "@/components/JTabs"
import JDialog from "@/components/JDialog"
import api from "../api/"
import get from "lodash.get"
import dayjs from "dayjs"
import gql from "@/api/gql.js"
import resolveApiErrors from "@/api/resolveApiErrors"

import TemplateAttachments from "../components/EmailMjmlTemplate/TemplateAttachments/"
import TemplateSettings from "../components/EmailMjmlTemplate/TemplateSettings"
import TemplateEditor from "../components/EmailMjmlTemplate/TemplateEditor"
import TemplateSendTest from "../components/EmailMjmlTemplate/TemplateSendTest"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		JButton,
		JBaseToggle,
		JCard,
		JTabs,
		JDialog,
		CoreFormInput,
		TemplateAttachments,
		TemplateSettings,
		TemplateEditor,
		TemplateSendTest
	},

	data() {
		return {
			template: {
				name: null,
				description: null,
				subject: null,
				mjml: "<mjml><mj-body></mj-body></mjml>",
				attachedTemplates: [],
				attachedFiles: [],
				graphQlQuery: ""
			},
			// list of fetched pdf templates
			fetchedPdfTemplates: null,
			// html and attachment bytes fetched from mjml render
			render: {
				html: null,
				attachments: null,
				errors: null
			},
			toggleSetup: "editor", // editor or attachments or settings
			togglePreview: "desktop", // desktop or mobile or send_test or liquid
			jsonData: null
		}
	},

	methods: {
		async updateTemplate() {
			const res = await api.updateEmailMjmlTemplate({
				templateId: this.$route.params.id,
				input: {
					name: this.template.name,
					mjml: this.template.mjml,
					subject: this.template.subject,
					description: this.template.description,
					graphQlQuery: this.template.graphQlQuery
				}
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)

			this.renderEmailMjml()
		},

		async deleteTemplate() {
			const res = await api.deleteTemplate({
				templateId: this.$route.params.id
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)
			else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("template_deleted")
					}
				})

			this.$router.push({ name: "TemplatesList" })
		},

		async fetchEmailTemplate() {
			const res = await api.listTemplates({
				where: { id: this.$route.params.id }
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)

			let fetchedTemplate = get(res, "data.templates.list[0]")
			this.template.name = fetchedTemplate.name
			this.template.description = fetchedTemplate.description
			this.template.subject = fetchedTemplate.subject
			this.template.mjml = fetchedTemplate.mjml
			this.template.attachedTemplates = fetchedTemplate.attachmentTemplates
			this.template.attachedFiles = fetchedTemplate.attachmentReferences
			this.template.graphQlQuery = fetchedTemplate.graphQlQuery

			this.renderEmailMjml()
		},

		async fetchPdfTemplates() {
			const res = await api.pdfTemplates({
				where: {
					or: [
						{
							type: "pdfDrawing"
						},
						{
							type: "wkhtmltopdf"
						}
					]
				}
			})
			if (res.errors) this.$store.dispatch("showError", res.errors)

			this.fetchedPdfTemplates = get(res, "data.templates.list")
				.filter(item => Object.keys(item).length !== 0)
				.map(item => {
					return {
						id: item.id,
						name: item.name
					}
				})
		},

		async renderEmailMjml() {
			// figure whether to render email based on variables or content
			let templateRenderParametersInput = this.computedGraphQlVariables
				? { name: "data", variablesJsonString: this.computedGraphQlVariables }
				: null

			const res = await api.renderEmailMjmlTemplate({
				templateId: this.$route.params.id,
				input: templateRenderParametersInput
			})

			// catch errors
			if (res.data.renderEmailMjmlTemplate.errors) {
				console.log("true")
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res.data.renderEmailMjmlTemplate.errors.toString()
					}
				})
				this.render.errors = res.data.renderEmailMjmlTemplate.errors
			}

			// store html string
			this.render = {
				html: res.data.renderEmailMjmlTemplate.value.html,
				errors: null
			}
		},

		updateMjml($event) {
			this.template.mjml = $event
			this.updateTemplate()
		},

		updateGraphQlQuery($event) {
			this.template.graphQlQuery = $event
			this.updateTemplate()
			this.getJsonDataFromQuery()
		},

		updateGraphQlVariables($event) {
			this.$store.commit("templates/SET_GRAPHQL_VARIABLES", $event)
			this.renderEmailMjml()
			this.getJsonDataFromQuery()
		}

		//TODO: Get JSON Data from query
		// Hidden for now
		// async getJsonDataFromQuery() {
		//   if (!this.computedGraphQlVariables) return
		// 	const res = await gql({
		//     query: this.template.graphQlQuery,
		//     variables: JSON.parse(this.computedGraphQlVariables)
		// 	})
		// 	this.jsonData = JSON.stringify(res)
		// }
	},

	computed: {
		computedRenderErrors() {
			return this.render.errors
		},

		computedGraphQlVariables() {
			return this.$store.state.templates.graphQlVariables
		},

		computedEditorPayload() {
			return {
				mjml: this.template.mjml ? this.template.mjml : "",
				graphQlQuery: this.template.graphQlQuery
					? this.template.graphQlQuery
					: "",
				graphQlVariables: this.computedGraphQlVariables
					? this.computedGraphQlVariables
					: "",
				jsonData: this.jsonData ? this.jsonData : ""
			}
		}
	},

	created() {
		this.fetchEmailTemplate()
		this.fetchPdfTemplates()
	},

	beforeDestroy() {
		this.$store.commit("templates/SET_GRAPHQL_VARIABLES", "")
	}
}
</script>
